import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";
import { createPortal } from "react-dom";
import { MoreHorizontal, type LucideIcon } from "lucide-react";
import { Spinner } from "./Spinner";

export type RowAction = {
  label: string;
  icon?: LucideIcon;
  onSelect: () => void;
  danger?: boolean;
  disabled?: boolean;
  /** Leave the action out for this row (e.g. "Reverse" on an unused code). */
  hidden?: boolean;
};

const ITEM_H = 36;

/**
 * Every row's actions behind one kebab button, so a table reads as data rather
 * than a wall of buttons. The menu is portalled to <body> because table regions
 * scroll and clip; it opens under the trigger (above it near the viewport's
 * bottom) and closes on any scroll or resize instead of chasing the row.
 * Danger actions sit last, below a rule.
 */
export function RowActions({
  items,
  label,
  busy = false,
}: {
  items: RowAction[];
  /** Names the row for assistive tech: "Actions for {label}". */
  label: string;
  busy?: boolean;
}) {
  const [pos, setPos] = useState<{ top?: number; bottom?: number; right: number } | null>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const visible = items.filter((i) => !i.hidden);
  const open = pos !== null;

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent): void => {
      const t = e.target as Node;
      if (!menu.current?.contains(t) && !trigger.current?.contains(t)) setPos(null);
    };
    const dismiss = (): void => setPos(null);
    document.addEventListener("mousedown", onDown);
    window.addEventListener("scroll", dismiss, true);
    window.addEventListener("resize", dismiss);
    menu.current?.querySelector<HTMLButtonElement>("[role=menuitem]:not(:disabled)")?.focus();
    return () => {
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("scroll", dismiss, true);
      window.removeEventListener("resize", dismiss);
    };
  }, [open]);

  if (visible.length === 0) return null;

  const close = (): void => {
    setPos(null);
    trigger.current?.focus();
  };

  const toggle = (): void => {
    if (open) return close();
    const r = trigger.current!.getBoundingClientRect();
    const right = window.innerWidth - r.right;
    const height = visible.length * ITEM_H + 16;
    setPos(
      r.bottom + height > window.innerHeight
        ? { bottom: window.innerHeight - r.top + 4, right }
        : { top: r.bottom + 4, right },
    );
  };

  const onMenuKey = (e: KeyboardEvent): void => {
    // Portal events still bubble to React ancestors — a clickable <tr> included.
    e.stopPropagation();
    if (e.key === "Escape" || e.key === "Tab") {
      // Tab: hand focus back to the trigger, then let the browser move on from it.
      if (e.key === "Escape") e.preventDefault();
      return close();
    }
    const all = [...(menu.current?.querySelectorAll<HTMLButtonElement>("[role=menuitem]:not(:disabled)") ?? [])];
    const at = all.indexOf(document.activeElement as HTMLButtonElement);
    const next = {
      ArrowDown: all[(at + 1) % all.length],
      ArrowUp: all[(at - 1 + all.length) % all.length],
      Home: all[0],
      End: all[all.length - 1],
    }[e.key];
    if (next) {
      e.preventDefault();
      next.focus();
    }
  };

  const firstDanger = visible.findIndex((i) => i.danger);

  return (
    <>
      <button
        aria-controls={open ? menuId : undefined}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={`Actions for ${label}`}
        className={`inline-grid size-8 place-items-center rounded-md text-muted transition-colors hover:bg-ink/[0.06] hover:text-ink disabled:pointer-events-none disabled:opacity-60 ${
          open ? "bg-ink/[0.06] text-ink" : ""
        }`}
        disabled={busy}
        onClick={(e) => {
          e.stopPropagation();
          toggle();
        }}
        onKeyDown={(e) => e.stopPropagation()}
        ref={trigger}
        type="button"
      >
        {busy ? <Spinner /> : <MoreHorizontal aria-hidden="true" className="size-4" />}
      </button>
      {open
        ? createPortal(
            <div
              aria-label={`Actions for ${label}`}
              className={`fixed z-50 min-w-44 animate-pop rounded-md border border-rule bg-raised p-1 text-left shadow-pop ${
                pos.top !== undefined ? "origin-top-right" : "origin-bottom-right"
              }`}
              id={menuId}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={onMenuKey}
              ref={menu}
              role="menu"
              style={pos}
            >
              {visible.map((item, i) => {
                const Icon = item.icon;
                return (
                  <div key={item.label}>
                    {i === firstDanger && i > 0 ? <div className="my-1 h-px bg-rule" role="separator" /> : null}
                    <button
                      className={`flex h-9 w-full items-center gap-2.5 rounded px-2.5 text-sm whitespace-nowrap outline-none disabled:pointer-events-none disabled:opacity-40 ${
                        item.danger
                          ? "text-danger hover:bg-danger/10 focus:bg-danger/10"
                          : "text-ink hover:bg-ink/[0.05] focus:bg-ink/[0.05]"
                      }`}
                      disabled={item.disabled}
                      onClick={() => {
                        close();
                        item.onSelect();
                      }}
                      role="menuitem"
                      tabIndex={-1}
                      type="button"
                    >
                      {Icon ? <Icon aria-hidden="true" className="size-4 shrink-0 opacity-70" /> : null}
                      {item.label}
                    </button>
                  </div>
                );
              })}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
